import { filter, reduce } from 'lodash';

type HeadLike = {
  splitType?: string | null;
  cutsheetInformation?: Array<{
    contactUser?: { _id?: string | null } | null;
    cutsheet?: { _id?: string | null } | null;
    questionnaireResponse?: { _id?: string | null } | null;
  } | null> | null;
} | null;

const EXPECTED_SLOT_COUNT: Record<string, number> = {
  whole: 1,
  half: 2,
  quarters: 4,
  half_and_two_quarters: 3,
};

function countMissingBuyers(h: HeadLike): number {
  if (!h?.splitType) return 0;
  const infos = h.cutsheetInformation ?? [];
  const expected = EXPECTED_SLOT_COUNT[h.splitType] ?? 1;

  // Slots not yet created count as missing buyers
  const missingSlots = Math.max(0, expected - infos.length);

  // Existing entries without a contactUser
  const missingInEntries = filter(infos, (ci) => !ci?.contactUser).length;

  return missingSlots + missingInEntries;
}

function countMissingCutsheets(h: HeadLike): number {
  if (!h?.splitType) return 0;
  return filter(
    h?.cutsheetInformation,
    (ci) => !ci?.cutsheet && !ci?.questionnaireResponse?._id,
  ).length;
}

export type AttentionCategory = 'Splits' | 'Buyers' | 'Cutsheets';

export type AttentionInfo = {
  totalIssues: number;
  categories: AttentionCategory[];
  /** The highest-priority missing category (Splits > Buyers > Cutsheets) */
  firstCategory: AttentionCategory | null;
  /** Count of items missing in the firstCategory */
  firstCategoryCount: number;
};

/**
 * Returns a summary of all attention issues across heads:
 * total issue count and which categories (Splits, Buyers, Cutsheets) are affected.
 */
export function getAttentionInfo(
  heads: HeadLike[] | null | undefined,
): AttentionInfo {
  if (!heads?.length)
    return {
      totalIssues: 0,
      categories: [],
      firstCategory: null,
      firstCategoryCount: 0,
    };

  const missingSplits = filter(heads, (h) => !h?.splitType).length;
  const missingBuyers = reduce(
    heads,
    (sum, h) => sum + countMissingBuyers(h),
    0,
  );
  const missingCutsheets = reduce(
    heads,
    (sum, h) => sum + countMissingCutsheets(h),
    0,
  );

  const totalIssues = missingSplits + missingBuyers + missingCutsheets;
  const categories: AttentionCategory[] = [];
  if (missingSplits > 0) categories.push('Splits');
  if (missingBuyers > 0) categories.push('Buyers');
  if (missingCutsheets > 0) categories.push('Cutsheets');

  // Hierarchical: Splits > Buyers > Cutsheets
  let firstCategory: AttentionCategory | null = null;
  let firstCategoryCount = 0;
  if (missingSplits > 0) {
    firstCategory = 'Splits';
    firstCategoryCount = missingSplits;
  } else if (missingBuyers > 0) {
    firstCategory = 'Buyers';
    firstCategoryCount = missingBuyers;
  } else if (missingCutsheets > 0) {
    firstCategory = 'Cutsheets';
    firstCategoryCount = missingCutsheets;
  }

  return { totalIssues, categories, firstCategory, firstCategoryCount };
}

/**
 * Returns buyer-specific attention info.
 * Only counts splits assigned to the given buyer that are missing a cutsheet.
 */
export function getBuyerAttentionInfo(
  heads: HeadLike[] | null | undefined,
  buyerId: string,
): AttentionInfo {
  if (!heads?.length)
    return {
      totalIssues: 0,
      categories: [],
      firstCategory: null,
      firstCategoryCount: 0,
    };

  const totalMissing = reduce(
    heads,
    (sum, h) => {
      if (!h?.splitType) return sum;
      return (
        sum +
        filter(
          h.cutsheetInformation,
          (ci) =>
            ci?.contactUser?._id === buyerId &&
            !ci?.cutsheet &&
            !ci?.questionnaireResponse?._id,
        ).length
      );
    },
    0,
  );

  return {
    totalIssues: totalMissing,
    categories: totalMissing > 0 ? ['Cutsheets'] : [],
    firstCategory: totalMissing > 0 ? 'Cutsheets' : null,
    firstCategoryCount: totalMissing,
  };
}

/**
 * Returns true if any split assigned to the buyer needs a cutsheet.
 */
export function jobNeedsBuyerAttention(
  heads: HeadLike[] | null | undefined,
  buyerId: string,
): boolean {
  return getBuyerAttentionInfo(heads, buyerId).totalIssues > 0;
}
